import { apiRequest } from './axiosConfig';
import { Chat, ChatMessage } from '../../types';
import type { ChatProductFocusPayload } from '../../utils/chatProductFocus';

/** チャットの画面種別。'v2' はチャットv2（選択肢カードで進める画面）。未指定は従来のチャット */
export type ChatInterface = 'v2';

export interface CreateChatRequest {
  // 未指定なら backend が既定エージェントを使う（チャットv2 は常に未指定）
  agent_id?: number;
  interface?: ChatInterface;
  initial_message?: string;
  uploaded_files?: {
    file_id: string;
    service: string;
    name: string;
    mime_type?: string;
    size?: number;
    status?: string;
  }[];
}

export interface SendMessageRequest {
  content: string;
  chat_id: number;
  has_attachments?: boolean;
  uploaded_files?: {
    file_id: string;
    service: string;
    name: string;
    mime_type?: string;
  }[];
  enable_search?: boolean; // 検索機能フラグを追加
  product_id?: number; // 商品ID（ナレッジ管理）
  enable_streaming?: boolean; // ストリーミングモードフラグ（GPT-5用）
  enable_deep_research?: boolean; // DeepResearchモード（GPT-5.2 Pro使用）
  generation_mode?: 'image' | 'video'; // 画像/動画モード（LLMを通さず生成）
  product_focus?: ChatProductFocusPayload; // 商品選択で選んだ対象・訴求・メイン画像（ID のみ。値は backend が商品データから解決）
}

class ChatService {
  /**
   * チャット一覧を取得
   * @returns ユーザーの全チャット履歴
   */
  async getUserChats(): Promise<Chat[]> {
    try {
      // tokenManagerからトークンを取得するように変更
      const tokenManager = (await import('../../utils/tokenManager')).default;
      const response = await apiRequest.get<{ chats: Chat[] }>('/users/chats');
      return response.chats || [];
    } catch (error) {
      throw error;
    }
  }
  
  /**
   * 画面種別ごとのチャット一覧を取得（GET /chats）。interface 未指定は従来のチャットのみ
   */
  async getChats(params: { interface?: ChatInterface } = {}): Promise<Chat[]> {
    const query = params.interface ? `?interface=${params.interface}` : '';
    const response = await apiRequest.get<{ chats: Chat[] }>(`/chats${query}`);
    return response.chats || [];
  }

  /**
   * 新しいチャットを作成
   * @param data 作成するチャット情報（AIエージェントID）
   * @returns 作成されたチャット、トークン使用量、初期メッセージがある場合はメッセージとレスポンス
   */
  async createChat(data: CreateChatRequest): Promise<{
    chat: Chat;
    tokens_used: number;
    remaining_tokens?: number;
    message?: ChatMessage;
    response?: ChatMessage;
  }> {
    try {
      return await apiRequest.post<{
        chat: Chat;
        tokens_used: number;
        remaining_tokens?: number;
        message?: ChatMessage;
        response?: ChatMessage;
      }>('/chats', data);
    } catch (error) {
      console.error('Failed to create chat:', error);
      throw error;
    }
  }
  
  /**
   * 特定のチャットを取得
   * @param chatId チャットID
   * @returns チャット情報とメッセージ履歴
   */
  async getChatById(chatId: number): Promise<Chat> {
    try {
      return await apiRequest.get<Chat>(`/chats/${chatId}`);
    } catch (error) {
      console.error(`Failed to get chat with id ${chatId}:`, error);
      throw error;
    }
  }
  
  /**
   * チャットにメッセージを送信（非同期版）
   * @param data 送信するメッセージ内容とチャットID
   * @returns メッセージIDとステータス
   */
  async sendMessage(data: SendMessageRequest): Promise<{
    user_message_id: number;
    ai_message_id: number;
    status: string;
    chat_id: number;
  }> {
    // chat_idはURLパスに含め、リクエストボディには含めない
    const { chat_id, content, has_attachments } = data;

    if (!chat_id || isNaN(Number(chat_id))) {
      console.error('sendMessageAsync: 無効なチャットID', chat_id);
      throw new Error('無効なチャットIDです');
    }

    // リクエストボディを構築
    const requestBody: any = { content };
    if (has_attachments) {
      requestBody.has_attachments = true;
    }
    if (data.uploaded_files && data.uploaded_files.length > 0) {
      requestBody.uploaded_files = data.uploaded_files;
    }
    if (data.enable_search) {
      requestBody.enable_search = true;
    }
    if (data.product_id) {
      requestBody.product_id = data.product_id;
    }
    if (data.enable_streaming) {
      requestBody.enable_streaming = true;
    }
    if (data.generation_mode) {
      requestBody.generation_mode = data.generation_mode;
    }
    if (data.product_id && data.product_focus) {
      requestBody.product_focus = data.product_focus;
    }
    if (data.enable_deep_research) {
      requestBody.enable_deep_research = true;
    }

    // 3回までリトライ
    const maxRetries = 3;
    let lastError: Error | null = null;

    for (let retryCount = 0; retryCount < maxRetries; retryCount++) {
      try {
        const response = await apiRequest.post<{
          user_message_id: number;
          ai_message_id: number;
          status: string;
          chat_id: number;
        }>(`/chats/${chat_id}/messages`, requestBody);

        return response;
      } catch (error) {
        lastError = error as Error;
        console.log(`メッセージ送信リトライ (${retryCount + 1}/${maxRetries}):`, error);

        if (retryCount < maxRetries - 1) {
          // リトライ前に1秒待機
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
      }
    }

    // 3回失敗した場合
    console.error('Failed to send message async after retries:', lastError);
    throw lastError;
  }

  
  /**
   * チャットを削除
   * @param chatId チャットID
   * @returns 成功したかどうか
   */
  async deleteChat(chatId: number): Promise<{ success: boolean; message: string }> {
    try {
      return await apiRequest.delete<{ success: boolean; message: string }>(`/chats/${chatId}`);
    } catch (error) {
      console.error(`Failed to delete chat with id ${chatId}:`, error);
      throw error;
    }
  }

  /**
   * チャットのブックマーク状態をトグル
   * @param chatId チャットID
   * @returns ブックマーク状態と成功メッセージ
   */
  async toggleBookmark(chatId: number): Promise<{ success: boolean; is_bookmarked: boolean; message: string }> {
    try {
      return await apiRequest.post<{ success: boolean; is_bookmarked: boolean; message: string }>(`/chats/${chatId}/bookmark`);
    } catch (error) {
      console.error(`Failed to toggle bookmark for chat id ${chatId}:`, error);
      throw error;
    }
  }

  /**
   * チャットの情報を更新（タイトルなど）
   * @param chatId チャットID
   * @param data 更新データ（タイトルなど）
   * @returns 更新されたチャット情報
   */
  async updateChat(
    chatId: number,
    data: { title: string }
  ): Promise<Chat> {
    try {
      return await apiRequest.put<Chat>(`/chats/${chatId}`, data);
    } catch (error) {
      console.error(`Failed to update chat with id ${chatId}:`, error);
      throw error;
    }
  }

  /**
   * チャットのタイトルを更新
   * @param chatId チャットID
   * @param title 新しいタイトル
   * @returns 更新されたチャット情報
   */
  async updateChatTitle(
    chatId: number,
    title: string
  ): Promise<{ success: boolean; data: Chat }> {
    try {
      return await apiRequest.put<{ success: boolean; data: Chat }>(`/chats/${chatId}/title`, { title });
    } catch (error) {
      console.error(`Failed to update chat title for chat id ${chatId}:`, error);
      throw error;
    }
  }
  
  /**
   * チャットメッセージ履歴をクリア（チャット自体は削除しない）
   * @param chatId チャットID
   * @returns 成功したかどうか
   */
  async clearChatHistory(chatId: number): Promise<{ success: boolean; message: string }> {
    try {
      return await apiRequest.delete<{ success: boolean; message: string }>(`/chats/${chatId}/messages`);
    } catch (error) {
      console.error(`Failed to clear chat history for chat id ${chatId}:`, error);
      throw error;
    }
  }

  /**
   * メッセージのステータスを取得
   * @param messageId メッセージID
   * @returns メッセージのステータス情報
   */
  async getMessageStatus(messageId: number): Promise<{
    message_id: number;
    status: 'pending' | 'processing' | 'completed' | 'failed';
    chat_id: number;
    role: string;
    created_at: string;
    content?: string;
    suggestions?: string[];
    tokens?: any;
    completed_at?: string;
    processing_started_at?: string;
    processing_completed_at?: string;
    processing_duration_seconds?: number;
    error_message?: string;
    assistant_message?: {
      id: number;
      content: string;
      role: string;
      timestamp: string;
      created_at: string;
    };
  }> {
    try {
      return await apiRequest.get<{
        message_id: number;
        status: 'pending' | 'processing' | 'completed' | 'failed';
        chat_id: number;
        role: string;
        created_at: string;
        content?: string;
        suggestions?: string[];
        tokens?: any;
        completed_at?: string;
        processing_started_at?: string;
        processing_completed_at?: string;
        processing_duration_seconds?: number;
        error_message?: string;
        assistant_message?: {
          id: number;
          content: string;
          role: string;
          timestamp: string;
          created_at: string;
        };
      }>(`/messages/${messageId}/status`);
    } catch (error) {
      console.error(`Failed to get message status for message id ${messageId}:`, error);
      throw error;
    }
  }
}

export default new ChatService();
